import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import CloseIcon from '@mui/icons-material/Close';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MuiSelect from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '../../Button/Button';
import DietTable from '../../DietTable/DietTable';
import AlternativesDietTable from '../../AlternativesDietTable/AlternativesDietTable';
import { useNotification } from '../../notifications/useNotification';
import './TemplateModal.css';

const LANGUAGE_OPTIONS = ['it', 'en', 'fr', 'de'];
const DIET_TYPE_OPTIONS = [
    'omnivore', 'vegan', 'vegetarian', 'carnivore', 'pescatarian',
    'mediterranean', 'ketogenic', 'paleo', 'gluten_free', 'lactose_free',
];

const textFieldSx = {
    '& .MuiOutlinedInput-root': {
        '&:hover fieldset': { borderColor: 'var(--secondary-color)' },
        '&.Mui-focused fieldset': { borderColor: 'var(--primary-color)' },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: 'var(--primary-color)' },
};

const toObjectIdArray = (arr) =>
    arr
        .map((item) => (typeof item === 'object' ? item?._id : item))
        .filter(Boolean);

const TemplateModal = ({
    mode = 'edit',
    template,
    allIntolerances,
    allIllnesses,
    setShowModal,
    onSave,
}) => {
    const { t, i18n } = useTranslation();
    const { showError, showSuccess } = useNotification();
    const lang = i18n.language?.startsWith('it') ? 'it' : 'en';
    const isCreateMode = mode === 'create';

    const [title, setTitle] = useState(template?.title ?? '');
    const [language, setLanguage] = useState(template?.language ?? lang);
    const [dietType, setDietType] = useState(template?.dietType ?? 'omnivore');
    const [intolerances, setIntolerances] = useState(template?.intolerances ?? []);
    const [illnesses, setIllnesses] = useState(template?.illnesses ?? []);
    const [saving, setSaving] = useState(false);
    const [editedDiet, setEditedDiet] = useState(null);

    const handleDietChange = (newDiet) => setEditedDiet(newDiet);

    const resolveLocalizedName = (item) => {
        if (!item) return '';
        if (typeof item === 'string') return item;
        if (typeof item.name === 'object') return item.name[lang] || item.name.en || item.name.it || '';
        return item.name ?? '';
    };

    const previewDiet = useMemo(() => ({
        _id: template?._id,
        dietPlan: template?.dietPlan ?? {},
        startDate: template?.createdAt ?? new Date().toISOString(),
        endDate: template?.updatedAt ?? template?.createdAt ?? new Date().toISOString(),
        language,
        isAlternatives: Boolean(template?.isAlternatives),
    }), [template, language]);

    const handleClose = () => setShowModal(null);

    const handleSave = async () => {
        if (!title.trim()) {
            showError(t('template-title-required'));
            return;
        }

        const payload = {
            title: title.trim(),
            language,
            dietPlan: editedDiet?.dietPlan ?? template?.dietPlan ?? {},
            intolerances: toObjectIdArray(intolerances),
            illnesses: toObjectIdArray(illnesses),
            isAlternatives: Boolean(template?.isAlternatives),
            dietType,
        };

        try {
            setSaving(true);

            if (isCreateMode) {
                await axios.post(
                    `${process.env.REACT_APP_BACKEND_URL}/diet-templates`,
                    payload,
                    { withCredentials: true }
                );
                showSuccess(t('template-created'));
            } else {
                await axios.put(
                    `${process.env.REACT_APP_BACKEND_URL}/diet-templates/${template._id}`,
                    payload,
                    { withCredentials: true }
                );
                showSuccess(t('template-updated'));
            }

            if (onSave) onSave();
            handleClose();
        } catch (error) {
            console.error(`Failed to ${isCreateMode ? 'create' : 'update'} template:`, error);
            showError(t('template-not-saved'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="modal-overlay">
            <div id="template-modal" className="modal-content">
                <div className="tm-header">
                    <h2>{isCreateMode ? t('new-template') : t('edit-template')}</h2>
                    <div className="tm-close" onClick={handleClose}>
                        <CloseIcon />
                    </div>
                </div>

                <div className="tm-fields">
                    <TextField
                        label={t('template-title')}
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        fullWidth
                        size="small"
                        sx={textFieldSx}
                    />

                    <FormControl fullWidth size="small" sx={textFieldSx}>
                        <InputLabel>{t('Language')}</InputLabel>
                        <MuiSelect
                            value={language}
                            label={t('Language')}
                            onChange={(e) => setLanguage(e.target.value)}
                        >
                            {LANGUAGE_OPTIONS.map((opt) => (
                                <MenuItem key={opt} value={opt}>
                                    {opt.toUpperCase()}
                                </MenuItem>
                            ))}
                        </MuiSelect>
                    </FormControl>

                    <FormControl fullWidth size="small" sx={textFieldSx}>
                        <InputLabel>{t('diet-type')}</InputLabel>
                        <MuiSelect
                            value={dietType}
                            label={t('diet-type')}
                            onChange={(e) => setDietType(e.target.value)}
                        >
                            {DIET_TYPE_OPTIONS.map((opt) => (
                                <MenuItem key={opt} value={opt}>
                                    {t(opt)}
                                </MenuItem>
                            ))}
                        </MuiSelect>
                    </FormControl>

                    <Autocomplete
                        multiple
                        size="small"
                        options={allIntolerances}
                        value={intolerances}
                        onChange={(_, value) => setIntolerances(value)}
                        getOptionLabel={(o) => resolveLocalizedName(o)}
                        isOptionEqualToValue={(option, value) => option._id === value._id}
                        renderInput={(params) => (
                            <TextField {...params} label={t('intolerances')} />
                        )}
                        sx={textFieldSx}
                    />

                    <Autocomplete
                        multiple
                        size="small"
                        options={allIllnesses}
                        value={illnesses}
                        onChange={(_, value) => setIllnesses(value)}
                        getOptionLabel={(o) => resolveLocalizedName(o)}
                        isOptionEqualToValue={(option, value) => option._id === value._id}
                        renderInput={(params) => (
                            <TextField {...params} label={t('illnesses')} />
                        )}
                        sx={textFieldSx}
                    />
                </div>

                <div className="tm-diet-preview">
                    {previewDiet.isAlternatives ? (
                        <AlternativesDietTable
                            diet={previewDiet}
                            editedDiet={editedDiet}
                            editing={true}
                            onDietChange={handleDietChange}
                            showInfoBar={false}
                        />
                    ) : (
                        <DietTable
                            diet={previewDiet}
                            editedDiet={editedDiet}
                            editing={true}
                            onDietChange={handleDietChange}
                            showInfoBar={false}
                        />
                    )}
                </div>

                <div className="tm-actions">
                    <Button label={t('Cancel')} onClick={handleClose} disabled={saving} />
                    <Button label={t('Save')} onClick={handleSave} disabled={saving} />
                </div>
            </div>
        </div>
    );
};

export default TemplateModal;